import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiSearch, FiRefreshCw, FiDatabase, FiAlertTriangle } from 'react-icons/fi';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  StatCard,
  CsvExportButton,
} from '../../components/UI';
import { usePagination } from '../../hooks';
import { listPayments, backfillPayments } from '../../services/adminService';
import styles from './Payments.module.css';

const RANGE_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 365, label: '1y' },
];

const PLATFORM_OPTIONS = [
  { value: 'all', label: 'All platforms' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'stripe', label: 'Stripe' },
];

const KIND_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'booster', label: 'Booster' },
  { value: 'vet_seat', label: 'Vet seat' },
  { value: 'premium_username', label: 'Premium username' },
  // The platform's cut of a shop order or vet booking — the application fee,
  // not the gross charge, which belongs to the seller.
  { value: 'marketplace_commission', label: 'Marketplace commission' },
];

const ENVIRONMENT_OPTIONS = [
  { value: 'all', label: 'All environments' },
  { value: 'production', label: 'Production only' },
  { value: 'sandbox', label: 'Sandbox / TestFlight' },
  { value: 'test', label: 'Play tester' },
  { value: 'promo', label: 'Play promo' },
  { value: 'unknown', label: 'Unknown' },
];

// Environments where no money changed hands. Mirrors NON_REVENUE_ENVIRONMENTS
// in functions/index.js — keep the two in step.
const NON_REVENUE_ENVIRONMENTS = ['sandbox', 'test', 'promo'];
const isNonRevenue = (environment) => NON_REVENUE_ENVIRONMENTS.includes(environment);

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString();
};

const formatMoney = (amount, currency = 'EUR') => {
  if (amount == null) return '-';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount));
  } catch {
    return `${amount} ${currency}`;
  }
};

const PLATFORM_VARIANT = { ios: 'primary', android: 'success', stripe: 'secondary' };
const EVENT_VARIANT = { purchase: 'success', renewal: 'primary', refund: 'danger' };

const Payments = () => {
  const [rangeDays, setRangeDays] = useState(90);
  const [platform, setPlatform] = useState('all');
  const [kind, setKind] = useState('all');
  const [environment, setEnvironment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Backfill flow — always previewed before it writes.
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listPayments({ platform, kind, environment, sinceDays: rangeDays });
      setPayments(Array.isArray(res?.payments) ? res.payments : []);
    } catch (err) {
      setError(err.message || 'Failed to load payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [platform, kind, environment, rangeDays]);

  useEffect(() => { load(); }, [load]);

  // Email search is applied client-side so typing doesn't re-hit the function.
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return payments;
    return payments.filter((p) =>
      String(p.userEmail || '').toLowerCase().includes(q) ||
      String(p.transactionId || '').toLowerCase().includes(q) ||
      String(p.productId || '').toLowerCase().includes(q)
    );
  }, [payments, searchQuery]);

  const { page, pageSize, pageCount, pageData, setPage, setPageSize, totalItems } =
    usePagination(filtered, 25);

  // Totals exclude sandbox, Play license-tester and Play promo rows — none of
  // them move money and none appear in the store's earnings report.
  const stats = useMemo(() => {
    const real = filtered.filter((p) => !isNonRevenue(p.environment));
    const gross = real.reduce((sum, p) => sum + (Number(p.amountEur) || 0), 0);
    const refunds = real
      .filter((p) => p.event === 'refund')
      .reduce((sum, p) => sum + Math.abs(Number(p.amountEur) || 0), 0);
    return {
      net: gross,
      refunds,
      count: real.length,
      estimates: real.filter((p) => p.isEstimate).length,
      nonRevenue: filtered.length - real.length,
    };
  }, [filtered]);

  const openBackfill = async () => {
    setBackfillOpen(true);
    setBackfillResult(null);
    setBackfillPreview(null);
    setBackfillBusy(true);
    try {
      setBackfillPreview(await backfillPayments({ dryRun: true }));
    } catch (err) {
      setBackfillPreview({ error: err.message });
    } finally {
      setBackfillBusy(false);
    }
  };

  const runBackfill = async () => {
    setBackfillBusy(true);
    try {
      const res = await backfillPayments({ dryRun: false });
      setBackfillResult(res);
      await load();
    } catch (err) {
      setBackfillResult({ error: err.message });
    } finally {
      setBackfillBusy(false);
    }
  };

  const columns = [
    {
      key: 'createdAt',
      title: 'Date',
      width: 170,
      render: (v, row) => formatDate(row.storePurchaseAt || v),
    },
    {
      key: 'userEmail',
      title: 'User',
      render: (v, row) => (
        <div>
          <div>{v || <span className={styles.muted}>unknown</span>}</div>
          <div className={styles.code}>{row.userId}</div>
        </div>
      ),
    },
    {
      key: 'kind',
      title: 'Type',
      width: 130,
      render: (v, row) => (
        <div className={styles.stack}>
          <Badge variant="default" size="sm">{v || '-'}</Badge>
          {row.event && (
            <Badge variant={EVENT_VARIANT[row.event] || 'default'} size="sm">{row.event}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'productId',
      title: 'Product',
      render: (v, row) => (
        <div>
          <div className={styles.code}>{v || '-'}</div>
          {row.tier && (
            <div className={styles.muted}>
              {row.tier}{row.billingCycle ? ` · ${row.billingCycle}` : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'platform',
      title: 'Platform',
      width: 130,
      render: (v, row) => (
        <div className={styles.stack}>
          <Badge variant={PLATFORM_VARIANT[v] || 'default'} size="sm">{v || '-'}</Badge>
          {isNonRevenue(row.environment) && (
            <Badge variant="warning" size="sm">{row.environment}</Badge>
          )}
          {row.environment === 'unknown' && (
            <Badge variant="default" size="sm">env?</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'amountEur',
      title: 'Amount',
      width: 150,
      render: (v, row) => (
        <div>
          <div className={row.event === 'refund' ? styles.negative : styles.amount}>
            {formatMoney(v, 'EUR')}
          </div>
          {row.currency && row.currency !== 'EUR' && (
            <div className={styles.muted}>{formatMoney(row.amount, row.currency)}</div>
          )}
          {/* fxRate null on a priced row means we never saw a real receipt —
              the figure is the EUR list price, not what the user was charged. */}
          {row.isEstimate && <Badge variant="warning" size="sm">est.</Badge>}
        </div>
      ),
    },
    {
      key: 'transactionId',
      title: 'Transaction',
      render: (v) => <span className={styles.code}>{v || '-'}</span>,
    },
  ];

  const csvColumns = [
    { key: 'createdAt', label: 'Date' },
    { key: 'userEmail', label: 'Email' },
    { key: 'userId', label: 'User ID' },
    { key: 'kind', label: 'Type' },
    { key: 'event', label: 'Event' },
    { key: 'productId', label: 'Product' },
    { key: 'tier', label: 'Tier' },
    { key: 'billingCycle', label: 'Billing cycle' },
    { key: 'platform', label: 'Platform' },
    { key: 'environment', label: 'Environment' },
    { key: 'amount', label: 'Amount (native)' },
    { key: 'currency', label: 'Currency' },
    { key: 'amountEur', label: 'Amount (EUR)' },
    { key: 'isEstimate', label: 'Is estimate' },
    { key: 'status', label: 'Status' },
    { key: 'transactionId', label: 'Transaction ID' },
    { key: 'source', label: 'Source' },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Payments</h1>
          <div className={styles.rangePicker}>
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={rangeDays === opt.value ? styles.active : ''}
                onClick={() => setRangeDays(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <p className={styles.subtitle}>
          Every membership, booster and vet-seat charge across iOS, Android and Stripe.
        </p>
      </div>

      <div className={styles.statsGrid}>
        <StatCard title="Net revenue (EUR)" value={formatMoney(stats.net)} color="primary" />
        <StatCard title="Transactions" value={stats.count} color="secondary" />
        <StatCard title="Refunded (EUR)" value={formatMoney(stats.refunds)} color="warning" />
        <StatCard title="Non-revenue (excluded)" value={stats.nonRevenue} color="warning" />
      </div>

      {stats.estimates > 0 && (
        <Card className={styles.notice}>
          <FiAlertTriangle className={styles.noticeIcon} />
          <span>
            {stats.estimates} of these rows are <strong>list-price estimates</strong>, not receipts
            — Google Play does not report a charged amount, and backfilled history predates the
            ledger. Treat them as approximate.
          </span>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Search email, transaction or product…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={FiSearch}
            />
          </div>
          <select className={styles.select} value={platform} onChange={(e) => setPlatform(e.target.value)}>
            {PLATFORM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className={styles.select} value={kind} onChange={(e) => setKind(e.target.value)}>
            {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className={styles.select} value={environment} onChange={(e) => setEnvironment(e.target.value)}>
            {ENVIRONMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <Button variant="ghost" size="sm" icon={<FiRefreshCw />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <CsvExportButton rows={filtered} columns={csvColumns} filename="dierend-payments" />
          <Button variant="ghost" size="sm" icon={<FiDatabase />} onClick={openBackfill}>
            Backfill
          </Button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <Table
          columns={columns}
          data={pageData}
          loading={loading}
          emptyMessage="No payments in this range. If you expect some, run Backfill to reconstruct pre-ledger history."
        />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={backfillOpen}
        onClose={() => setBackfillOpen(false)}
        title="Backfill payment ledger"
      >
        <div className={styles.modalBody}>
          <p>
            Reconstructs ledger rows for purchases made before the ledger existed, from user
            documents and their subscription history.
          </p>
          <p className={styles.warn}>
            Backfilled rows are <strong>estimates</strong>: the real charged amount and the
            sandbox/production flag cannot be recovered after the fact, so they are priced from the
            EUR list table and marked <code>env?</code> and <code>est.</code> Re-running is safe —
            rows use deterministic ids, so it converges instead of duplicating.
          </p>
          <p className={styles.muted}>
            Internal and test accounts are excluded. Their sandbox/TestFlight history would
            otherwise be booked as real revenue, since backfilled rows can't recover the
            sandbox flag.
          </p>

          {backfillBusy && <p>Working…</p>}

          {backfillPreview?.error && (
            <div className={styles.error}>{backfillPreview.error}</div>
          )}

          {backfillPreview && !backfillPreview.error && !backfillResult && (
            <div className={styles.previewBox}>
              <div>
                <strong>{backfillPreview.uniqueCount ?? backfillPreview.plannedCount}</strong> ledger
                rows would be created
              </div>
              <div className={styles.muted}>
                from {backfillPreview.plannedCount} history entries — duplicates collapse via
                deterministic ids
              </div>
              <div className={styles.muted}>
                {backfillPreview.usersScanned} users scanned · {backfillPreview.skippedFree} on the
                free tier · {backfillPreview.skippedInternal ?? 0} internal/test accounts excluded
              </div>
            </div>
          )}

          {backfillResult && !backfillResult.error && (
            <div className={styles.previewBox}>
              <div><strong>{backfillResult.written}</strong> of {backfillResult.plannedCount} rows written</div>
              {backfillResult.failures?.length > 0 && (
                <div className={styles.warn}>{backfillResult.failures.length} failed — check function logs</div>
              )}
            </div>
          )}
          {backfillResult?.error && <div className={styles.error}>{backfillResult.error}</div>}

          <div className={styles.modalActions}>
            <Button variant="ghost" onClick={() => setBackfillOpen(false)}>Close</Button>
            <Button
              variant="primary"
              onClick={runBackfill}
              disabled={backfillBusy || !backfillPreview || backfillPreview.error || !!backfillResult}
            >
              Write {backfillPreview?.uniqueCount ?? backfillPreview?.plannedCount ?? ''} rows
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Payments;
